import { documentsDic } from "src/utils/dataDictionary";
import { formatMoneyWithCurrency } from "src/utils/formatter";

const PERIODO_VIGENTE = "Vigente";

export function getInitials(name, email) {
  const source = (name || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function avatarColorFromString(str) {
  const s = String(str || "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hues = [210, 250, 280, 160, 30, 350];
  const hue = hues[Math.abs(hash) % hues.length];
  return `hsl(${hue}, 55%, 42%)`;
}

export function getDocInnerCommentsList(doc) {
  const list = doc?.innerComments;
  return Array.isArray(list) ? list : [];
}

export function getClientCommentsCount(row) {
  return (
    row?.agenda?.clientCommentsCount ??
    row?.agenda?.collectionComments?.length ??
    0
  );
}

export function getInvoiceCommentsCount(row, docs) {
  if (row?.agenda?.invoiceCommentsCount != null) {
    return row.agenda.invoiceCommentsCount;
  }
  if (row?.invoiceCommentsCount != null) {
    return row.invoiceCommentsCount;
  }
  return (docs || []).reduce(
    (sum, d) => sum + getDocInnerCommentsList(d).length,
    0
  );
}

/** Resumen para un solo botón de notas (cliente + facturas). */
export function getClientNotesSummary(row, docs = []) {
  const clientCount = getClientCommentsCount(row);
  const invoiceCount = getInvoiceCommentsCount(row, docs);
  const docsWithNotes = (docs || []).filter((d) => getDocInnerCommentsList(d).length > 0);
  return {
    clientCount,
    invoiceCount,
    total: clientCount + invoiceCount,
    docsWithNotes,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfLocalDay(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function diffCalendarDays(fromDate, toDate) {
  const from = startOfLocalDay(fromDate);
  const to = startOfLocalDay(toDate);
  if (!from || !to) return null;
  return Math.round((to.getTime() - from.getTime()) / DAY_MS);
}

/** Estado vigente/vencido según vencimiento vs hoy (no el tramo del informe al corte del filtro). */
export function getEstadoInfo(doc, t) {
  const due = doc?.originalDueDate || doc?.dueDate;
  const today = startOfLocalDay(new Date());
  const dueDay = startOfLocalDay(due);

  if (!dueDay || !today) {
    return { label: "—", variant: "vencido" };
  }

  const overdueDays =
    doc?.diasAtraso != null && !Number.isNaN(Number(doc.diasAtraso))
      ? Math.round(Number(doc.diasAtraso))
      : diffCalendarDays(dueDay, today);

  if (overdueDays == null) {
    return { label: "—", variant: "vencido" };
  }

  if (overdueDays < 0) {
    const daysToDue = Math.abs(overdueDays);
    if (daysToDue === 1) return { label: t("estado.currentOneDay"), variant: "vigente" };
    return { label: t("estado.currentDays", { count: daysToDue }), variant: "vigente" };
  }

  if (overdueDays === 0) {
    return { label: t("estado.currentZeroDays"), variant: "vigente" };
  }

  if (overdueDays === 1) return { label: t("estado.overdueOneDay"), variant: "vencido" };
  return { label: t("estado.overdueDays", { count: overdueDays }), variant: "vencido" };
}

function formatDiasLabel(count, t) {
  const n = Math.abs(Math.round(Number(count)));
  if (Number.isNaN(n)) return "—";
  return t("dso.days", { count: n });
}

function formatFacturasLabel(count, t) {
  const n = Number(count);
  return t("dso.paidInvoices", { count: n });
}

export function getDsoPresentation(row, t) {
  const histN = Number(row?.dsoHistoryCount ?? row?.dpoHistoryCount ?? 0);
  const raw = row?.dsoDays ?? row?.dpoDays;
  if (raw == null || raw === "") {
    return { text: "—", className: "cf-dso-value--neutral", title: "" };
  }
  const num = Number(raw);
  if (Number.isNaN(num)) {
    return { text: "—", className: "cf-dso-value--neutral", title: "" };
  }
  if (histN > 0) {
    const abs = Math.abs(Math.round(num));
    const diasText = formatDiasLabel(abs, t);
    const facturasText = formatFacturasLabel(histN, t);
    if (num < 0) {
      return {
        text: diasText,
        className: "cf-dso-value--early",
        title: t("dso.avgEarly", { invoices: facturasText, days: diasText }),
      };
    }
    if (num > 0) {
      return {
        text: diasText,
        className: "cf-dso-value--late",
        title: t("dso.avgLate", { invoices: facturasText, days: diasText }),
      };
    }
    return {
      text: t("dso.zeroDays"),
      className: "cf-dso-value--neutral",
      title: t("dso.avgOnDue", { invoices: facturasText }),
    };
  }
  const rounded = Math.round(num);
  return {
    text: formatDiasLabel(rounded, t),
    className: "cf-dso-value--neutral",
    title: t("dso.contactOnly"),
  };
}

export function getCobranzaDocumentTypeLabel(doc) {
  const explicitLabel = String(
    doc?.summary || doc?.typeLabel || doc?.documentTypeName || ""
  ).trim();
  if (explicitLabel) return explicitLabel;

  const rawCode = doc?.documentCode ?? doc?.meta?.documentCode;
  if (rawCode != null && rawCode !== "") {
    const normalizedCode =
      typeof rawCode === "string" && Number.isNaN(Number(rawCode))
        ? rawCode
        : Number(rawCode);
    const dictionaryEntry = documentsDic?.[normalizedCode];
    if (dictionaryEntry?.fullName) return dictionaryEntry.fullName;
    if (dictionaryEntry?.name) return dictionaryEntry.name;
  }

  if (doc?.documentType === "OBLIGATION") return "Obligación";
  return "Documento";
}

export function getCobranzaDocumentLabel(doc) {
  const typeLabel = getCobranzaDocumentTypeLabel(doc);
  const folio = doc?.documentId || doc?.folio || doc?.meta?.documentId || doc?.meta?.number;
  return folio ? `${typeLabel} #${folio}` : typeLabel;
}

function getValidDateTs(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
}

function getDocumentNumericRef(doc) {
  const rawRef = doc?.documentId || doc?.folio || doc?.meta?.documentId || doc?.meta?.number;
  const num = Number(rawRef);
  return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
}

export function sortCobranzaDocsOldestFirst(docs = []) {
  return [...(docs || [])].sort((a, b) => {
    const aPrimaryTs = getValidDateTs(a?.originalDueDate || a?.dueDate || a?.documentDate);
    const bPrimaryTs = getValidDateTs(b?.originalDueDate || b?.dueDate || b?.documentDate);
    if (aPrimaryTs !== bPrimaryTs) return aPrimaryTs - bPrimaryTs;

    const aDocumentTs = getValidDateTs(a?.documentDate);
    const bDocumentTs = getValidDateTs(b?.documentDate);
    if (aDocumentTs !== bDocumentTs) return aDocumentTs - bDocumentTs;

    const aRef = getDocumentNumericRef(a);
    const bRef = getDocumentNumericRef(b);
    if (aRef !== bRef) return aRef - bRef;

    return String(getCobranzaDocumentLabel(a)).localeCompare(String(getCobranzaDocumentLabel(b)));
  });
}

const GESTION_STATE_ICONS = {
  COBRAR: "check-circle",
  NO_COBRAR: "x-circle",
  REVISAR: "alert-circle",
  LLAMAR: "phone",
  CORREO: "mail",
  NO_APLICA: "minus-circle",
};

function withGestionStateIcon(option) {
  const icon =
    option.value == null || option.value === "__NONE__"
      ? "minus"
      : GESTION_STATE_ICONS[option.value] || "minus";
  return { ...option, icon };
}

export function getDecisionOptions(t) {
  return [
    { value: "COBRAR", label: t("gestionDecision.collect"), className: "cf-gestion-badge--cobrar" },
    { value: "NO_COBRAR", label: t("gestionDecision.noCollect"), className: "cf-gestion-badge--no-cobrar" },
    { value: "REVISAR", label: t("gestionDecision.review"), className: "cf-gestion-badge--revisar" },
  ].map(withGestionStateIcon);
}

export function getChannelOptions(t) {
  return [
    { value: "LLAMAR", label: t("gestionDecision.call"), className: "cf-gestion-badge--llamar" },
    { value: "CORREO", label: t("gestionDecision.email"), className: "cf-gestion-badge--correo" },
    { value: "NO_APLICA", label: t("gestionDecision.notApplicable"), className: "cf-gestion-badge--no-aplica" },
  ].map(withGestionStateIcon);
}

export const DECISION_OPTIONS = [
  { value: "COBRAR", label: "Cobrar", className: "cf-gestion-badge--cobrar" },
  { value: "NO_COBRAR", label: "No cobrar", className: "cf-gestion-badge--no-cobrar" },
  { value: "REVISAR", label: "Revisar", className: "cf-gestion-badge--revisar" },
].map(withGestionStateIcon);

export const CHANNEL_OPTIONS = [
  { value: "LLAMAR", label: "Llamar", className: "cf-gestion-badge--llamar" },
  { value: "CORREO", label: "Correo", className: "cf-gestion-badge--correo" },
  { value: "NO_APLICA", label: "No aplica", className: "cf-gestion-badge--no-aplica" },
].map(withGestionStateIcon);

export function getGestionOptionPresentation(options, value) {
  const match = options.find((opt) => opt.value === value);
  const className = match?.className || "cf-gestion-badge--neutral";
  const icon =
    value == null || value === "__NONE__"
      ? "minus"
      : match?.icon || GESTION_STATE_ICONS[value] || "minus";
  return {
    label: match?.label || "—",
    className,
    icon,
  };
}

const CLEAR_OPTION = withGestionStateIcon({
  value: null,
  label: "Sin selección",
  className: "cf-gestion-badge--neutral",
});

export const DECISION_OPTIONS_WITH_CLEAR = [CLEAR_OPTION, ...DECISION_OPTIONS];
export const CHANNEL_OPTIONS_WITH_CLEAR = [CLEAR_OPTION, ...CHANNEL_OPTIONS];

export const COBRANZA_DECISION_FILTER_OPTIONS = [
  withGestionStateIcon({
    value: "__NONE__",
    label: "Sin selección",
    className: "cf-gestion-badge--neutral",
  }),
  ...DECISION_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    className: opt.className,
    icon: opt.icon,
  })),
];

export const COBRANZA_CHANNEL_FILTER_OPTIONS = [
  withGestionStateIcon({
    value: "__NONE__",
    label: "Sin selección",
    className: "cf-gestion-badge--neutral",
  }),
  ...CHANNEL_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
    className: opt.className,
    icon: opt.icon,
  })),
];

export function getCobranzaRowAssigneeUserId(row) {
  return String(
    row?.agenda?.collectionAssigneeUserId || row?.agenda?.collectionAssignee?.userId || ""
  ).trim();
}

export function rowMatchesCobranzaAssigneeFilters(row, filters = {}, currentUserId = "") {
  const selected = filters.assigneeUserIds;
  if (!Array.isArray(selected) || selected.length === 0) return true;

  const assigneeId = getCobranzaRowAssigneeUserId(row);
  return selected.some((filterId) => {
    if (filterId === "__NONE__") return !assigneeId;
    if (filterId === "__ME__") {
      return Boolean(currentUserId) && assigneeId === String(currentUserId);
    }
    return assigneeId === String(filterId);
  });
}

export function rowMatchesCobranzaManagementFilters(row, docs = [], filters = {}) {
  const decisions = filters.collectionDecisions;
  const channels = filters.collectionChannels;
  const hasDecisionFilter = Array.isArray(decisions) && decisions.length > 0;
  const hasChannelFilter = Array.isArray(channels) && channels.length > 0;
  if (!hasDecisionFilter && !hasChannelFilter) return true;

  const display = getCobranzaClientManagementDisplay(row?.agenda, docs);
  const decision = display.collectionDecision || null;
  const channel = display.collectionChannel || null;

  if (hasDecisionFilter) {
    const wantsEmpty = decisions.includes("__NONE__");
    const selected = decisions.filter((item) => item !== "__NONE__");
    const matchesEmpty = wantsEmpty && !decision;
    const matchesValue = decision && selected.includes(decision);
    if (!matchesEmpty && !matchesValue) return false;
  }

  if (hasChannelFilter) {
    const wantsEmpty = channels.includes("__NONE__");
    const selected = channels.filter((item) => item !== "__NONE__");
    const matchesEmpty = wantsEmpty && !channel;
    const matchesValue = channel && selected.includes(channel);
    if (!matchesEmpty && !matchesValue) return false;
  }

  return true;
}

export function applyCobranzaDocumentOverrides(docs = [], agenda) {
  const openDocIds = new Set(
    (docs || [])
      .filter((doc) => Number(doc?.remaining || 0) > 0 && doc?.id != null)
      .map((doc) => String(doc.id))
  );
  const overrides = Array.isArray(agenda?.collectionDocumentOverrides)
    ? agenda.collectionDocumentOverrides
    : [];
  const overridesById = new Map(
    overrides
      .filter(
        (override) =>
          override?.documentId != null && openDocIds.has(String(override.documentId))
      )
      .map((override) => [String(override.documentId), override])
  );

  return (docs || []).map((doc) => {
    const docId = String(doc?.id || "");
    const override = overridesById.get(docId);
    if (!override) return doc;

    return {
      ...doc,
      ...(override.collectionDecision !== undefined
        ? { collectionDecision: override.collectionDecision }
        : {}),
      ...(override.collectionChannel !== undefined
        ? { collectionChannel: override.collectionChannel }
        : {}),
      ...(override.collectionResponse !== undefined
        ? { collectionResponse: override.collectionResponse }
        : {}),
      ...(override.collectionNextSteps !== undefined
        ? { collectionNextSteps: override.collectionNextSteps }
        : {}),
    };
  });
}

export function getCobranzaDerivedClientManagement(docs = []) {
  const openDocs = (docs || []).filter((doc) => Number(doc?.remaining || 0) > 0);
  const docsWithDecision = openDocs.filter((doc) => Boolean(doc?.collectionDecision));
  const cobrarDocs = docsWithDecision.filter((doc) => doc.collectionDecision === "COBRAR");
  const revisarDocs = docsWithDecision.filter((doc) => doc.collectionDecision === "REVISAR");

  let collectionDecision = null;
  if (cobrarDocs.length > 0) {
    collectionDecision = "COBRAR";
  } else if (revisarDocs.length > 0) {
    collectionDecision = "REVISAR";
  } else if (
    docsWithDecision.length > 0 &&
    docsWithDecision.every((doc) => doc.collectionDecision === "NO_COBRAR")
  ) {
    collectionDecision = "NO_COBRAR";
  }

  const channelCounts = cobrarDocs.reduce((acc, doc) => {
    const channel = doc?.collectionChannel;
    if (!channel) return acc;
    acc[channel] = (acc[channel] || 0) + 1;
    return acc;
  }, {});

  let majorityChannel = null;
  let maxCount = 0;
  let hasTie = false;

  Object.entries(channelCounts).forEach(([channel, count]) => {
    if (count > maxCount) {
      majorityChannel = channel;
      maxCount = count;
      hasTie = false;
      return;
    }
    if (count === maxCount && count > 0) {
      hasTie = true;
    }
  });

  return {
    collectionDecision,
    collectionChannel: hasTie ? null : majorityChannel,
  };
}

/** Decisión/canal del cliente: solo facturas con saldo abierto (no agenda histórica). */
export function getCobranzaClientManagementDisplay(agenda, docs = []) {
  const docsWithOverrides = applyCobranzaDocumentOverrides(docs, agenda);
  return getCobranzaDerivedClientManagement(docsWithOverrides);
}

/** Navega a Configuración → Equipo (misma sección que Ajustes de la empresa). */
export function goToConfiguracionEquipo(history, { newTab = false } = {}) {
  try {
    localStorage.setItem("settings.activePanel", "equipo");
  } catch {
    /* ignore */
  }
  const path = "/configuracion#equipo";
  if (newTab && typeof window !== "undefined") {
    const url = `${window.location.origin}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  if (history?.push) {
    history.push(path);
  } else if (typeof window !== "undefined") {
    window.location.assign(path);
  }
}

export function formatGestionHistoryDateTime(date) {
  if (!date) return { dateLabel: "—", timeLabel: "00:00" };
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return { dateLabel: "—", timeLabel: "00:00" };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    dateLabel: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    timeLabel: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export function formatCobranzaEmailTypeLabel(emailType, t) {
  const key = String(emailType || "").toLowerCase();
  if (key === "por_vencer") return t("gestionHistory.emailType.dueSoon");
  if (key === "por_vencer_14d") return t("gestionHistory.emailType.dueSoonPlusOverdue");
  if (key === "al_vencimiento") return t("gestionHistory.emailType.onDueDate");
  if (key === "vencidas") return t("gestionHistory.emailType.overdue");
  if (key === "semana_previa") return t("gestionHistory.emailType.weekBefore");
  if (key === "dia_mes" || key === "frecuencia") return t("gestionHistory.emailType.frequency");
  return t("gestionHistory.emailType.default");
}

export function getCobranzaEmailHistorySummary(entry, t) {
  const subject = String(entry?.subject || entry?.metadata?.subject || "").trim();
  return subject || t("gestionHistory.emailDefault");
}

export function getCobranzaEmailHistoryBody(entry) {
  const bodyHtml = String(entry?.bodyHtml || entry?.metadata?.bodyHtml || "").trim();
  return bodyHtml || "";
}

export function getCobranzaEmailStatusPresentation(status, t) {
  const key = String(status || "").trim().toLowerCase();
  const map = {
    send: {
      label: t("gestionHistory.emailStatus.send"),
      className: "cf-gestion-email-status--send",
    },
    delivery: {
      label: t("gestionHistory.emailStatus.delivered"),
      className: "cf-gestion-email-status--delivery",
    },
    open: {
      label: t("gestionHistory.emailStatus.opened"),
      className: "cf-gestion-email-status--open",
    },
    click: {
      label: t("gestionHistory.emailStatus.clicked"),
      className: "cf-gestion-email-status--click",
    },
    bounce: {
      label: t("gestionHistory.emailStatus.bounce"),
      className: "cf-gestion-email-status--failed",
    },
    complaint: {
      label: t("gestionHistory.emailStatus.complaint"),
      className: "cf-gestion-email-status--failed",
    },
    reject: {
      label: t("gestionHistory.emailStatus.rejected"),
      className: "cf-gestion-email-status--failed",
    },
  };

  return (
    map[key] || {
      label: t("gestionHistory.emailStatus.pending"),
      className: "cf-gestion-email-status--pending",
    }
  );
}

const RECIPIENT_ROLE_ORDER = { to: 0, cc: 1 };

const COBRANZA_EMAIL_STATUS_RANK = {
  pending: 0,
  send: 1,
  delivery: 2,
  open: 3,
  click: 4,
  bounce: -2,
  complaint: -2,
  reject: -2,
};

function rankCobranzaEmailStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return COBRANZA_EMAIL_STATUS_RANK[key] ?? 0;
}

function pickHigherCobranzaEmailStatus(current, incoming) {
  const currentKey = String(current || "pending").trim().toLowerCase() || "pending";
  const incomingKey = String(incoming || "pending").trim().toLowerCase() || "pending";
  return rankCobranzaEmailStatus(incomingKey) >= rankCobranzaEmailStatus(currentKey)
    ? incomingKey
    : currentKey;
}

function mergeEmailRecipientsForHistory(notificationRecipients = [], managementRecipients = []) {
  const map = new Map();

  (managementRecipients || []).forEach((recipient) => {
    const key = String(recipient?.email || "").trim().toLowerCase();
    if (!key) return;
    map.set(key, { ...recipient });
  });

  (notificationRecipients || []).forEach((recipient) => {
    const key = String(recipient?.email || "").trim().toLowerCase();
    if (!key) return;
    const previous = map.get(key) || {};
    const tracked = isCobranzaTrackedEmailRecipient(recipient);
    const mergedStatus = tracked
      ? pickHigherCobranzaEmailStatus(previous.emailStatus, recipient?.emailStatus)
      : undefined;
    map.set(key, {
      ...previous,
      ...recipient,
      ...(tracked
        ? {
            emailStatus: mergedStatus,
            emailStatusAt: recipient?.emailStatusAt || previous.emailStatusAt,
          }
        : {
            emailStatus: undefined,
            emailStatusAt: undefined,
          }),
    });
  });

  return sortCobranzaEmailRecipients(Array.from(map.values()));
}

function applyEntryLevelEmailStatusFallback(recipients, entry) {
  const entryStatus = String(entry?.emailStatus || "").trim().toLowerCase();
  const entryStatusAt = entry?.emailStatusAt;
  if (!entryStatus || entryStatus === "pending") return recipients;

  return recipients.map((recipient) => {
    if (!isCobranzaTrackedEmailRecipient(recipient)) {
      return {
        ...recipient,
        emailStatus: undefined,
        emailStatusAt: undefined,
      };
    }

    const recipientStatus = String(recipient?.emailStatus || "pending").trim().toLowerCase();
    if (rankCobranzaEmailStatus(recipientStatus) > 0) return recipient;

    return {
      ...recipient,
      emailStatus: entryStatus,
      emailStatusAt: entryStatusAt || recipient?.emailStatusAt,
    };
  });
}

export function isCobranzaTrackedEmailRecipient(recipient) {
  return String(recipient?.role || "to").toLowerCase() === "to";
}

export function getCobranzaEmailRecipients(entry) {
  let recipients = [];
  if (Array.isArray(entry?.emailRecipients) && entry.emailRecipients.length > 0) {
    recipients = entry.emailRecipients;
  } else if (
    Array.isArray(entry?.metadata?.emailRecipients) &&
    entry.metadata.emailRecipients.length > 0
  ) {
    recipients = entry.metadata.emailRecipients;
  } else {
    const toEmail = String(entry?.recipientEmail || entry?.metadata?.recipientEmail || "").trim();
    const ccEmails = Array.isArray(entry?.ccEmails)
      ? entry.ccEmails
      : Array.isArray(entry?.metadata?.ccEmails)
        ? entry.metadata.ccEmails
        : [];

    if (toEmail) {
      recipients.push({
        email: toEmail,
        role: "to",
        emailStatus: entry?.emailStatus,
        emailStatusAt: entry?.emailStatusAt,
      });
    }

    ccEmails.forEach((email) => {
      const normalized = String(email || "").trim();
      if (!normalized) return;
      recipients.push({
        email: normalized,
        role: "cc",
      });
    });
  }

  return applyEntryLevelEmailStatusFallback(recipients, entry);
}

export function sortCobranzaEmailRecipients(recipients = []) {
  return [...recipients].sort((a, b) => {
    const roleA = String(a?.role || "cc").toLowerCase();
    const roleB = String(b?.role || "cc").toLowerCase();
    const orderA = RECIPIENT_ROLE_ORDER[roleA] ?? 1;
    const orderB = RECIPIENT_ROLE_ORDER[roleB] ?? 1;
    if (orderA !== orderB) return orderA - orderB;

    const teamA = String(a?.teamName || "").toLowerCase();
    const teamB = String(b?.teamName || "").toLowerCase();
    if (teamA !== teamB) return teamA.localeCompare(teamB);

    const labelA = String(a?.name || a?.email || "").toLowerCase();
    const labelB = String(b?.name || b?.email || "").toLowerCase();
    return labelA.localeCompare(labelB);
  });
}

function isPlaceholderRecipientName(name) {
  const value = String(name || "").trim();
  return !value || value === "--" || value === "—";
}

export function getCobranzaEmailRecipientLabel(recipient, t) {
  const email = String(recipient?.email || "").trim();
  const name = String(recipient?.name || "").trim();
  const role = String(recipient?.role || "").toLowerCase();
  const teamName = String(recipient?.teamName || "").trim();
  const roleLabel =
    role === "to"
      ? t("gestionHistory.recipientRole.to")
      : t("gestionHistory.recipientRole.cc");

  const displayName = !isPlaceholderRecipientName(name) ? name : "";
  const identity = displayName && displayName !== email ? displayName : email;

  if (teamName && recipient?.sourceType === "team") {
    return displayName
      ? `${identity} · ${teamName}`
      : `${email} · ${teamName}`;
  }

  return identity || email || roleLabel;
}

export function getCobranzaEmailRecipientRoleLabel(recipient, t) {
  const role = String(recipient?.role || "").toLowerCase();
  return role === "to"
    ? t("gestionHistory.recipientRole.to")
    : t("gestionHistory.recipientRole.cc");
}

export function getCobranzaEmailHistoryFacts(entry, t) {
  const facts = [];
  const emailRecipients = getCobranzaEmailRecipients(entry);
  const recipient = String(entry?.recipientEmail || entry?.metadata?.recipientEmail || "").trim();
  const ccEmails = Array.isArray(entry?.ccEmails)
    ? entry.ccEmails
    : Array.isArray(entry?.metadata?.ccEmails)
      ? entry.metadata.ccEmails
      : [];
  const emailType = entry?.emailType ?? entry?.metadata?.emailType;
  const invoiceCount = entry?.invoiceCount ?? entry?.metadata?.invoiceCount;
  const totalAmount = entry?.totalAmount ?? entry?.metadata?.totalAmount;

  if (!emailRecipients.length && recipient) {
    facts.push({ label: t("gestionHistory.to"), value: recipient });
  }
  if (!emailRecipients.length && ccEmails.length > 0) {
    facts.push({ label: "CC", value: ccEmails.join(", ") });
  }
  if (emailType) facts.push({ label: t("gestionHistory.type"), value: formatCobranzaEmailTypeLabel(emailType, t) });
  if (invoiceCount != null) {
    facts.push({
      label: t("gestionHistory.invoices"),
      value: `${invoiceCount} ${t("gestionHistory.invoiceCommitted", { count: Number(invoiceCount) })}`,
    });
  }
  if (totalAmount != null && totalAmount !== "") {
    facts.push({
      label: t("gestionHistory.amount"),
      value: formatMoneyWithCurrency(Number(totalAmount || 0), "CLP"),
    });
  }

  return facts;
}

export function getCobranzaEmailHistoryInvoices(entry, t) {
  const raw = Array.isArray(entry?.invoices)
    ? entry.invoices
    : Array.isArray(entry?.metadata?.invoices)
      ? entry.metadata.invoices
      : [];

  return raw
    .map((invoice) => {
      const label = String(invoice?.documentLabel || "").trim();
      const dueDate = invoice?.dueDate;
      const amount = invoice?.amount;
      return {
        documentId: invoice?.documentId ? String(invoice.documentId) : "",
        label: label || (t ? t("gestionHistory.document") : "Documento"),
        dueDateLabel: dueDate ? formatGestionHistoryDateTime(dueDate).dateLabel : "—",
        amountLabel:
          amount != null && amount !== ""
            ? formatMoneyWithCurrency(Number(amount || 0), "CLP")
            : "",
      };
    })
    .filter((invoice) => invoice.label || invoice.amountLabel);
}

function getCobranzaEmailEntryFromManagementEvent(event) {
  const metadata = event?.metadata || {};
  return {
    date: event?.date,
    userEmail: event?.userEmail || event?.userName,
    userId: event?.userId,
    recipientEmail: metadata?.recipientEmail,
    ccEmails: Array.isArray(metadata?.ccEmails) ? metadata.ccEmails : [],
    emailRecipients: Array.isArray(metadata?.emailRecipients) ? metadata.emailRecipients : [],
    subject: metadata?.subject,
    bodyHtml: metadata?.bodyHtml,
    emailType: metadata?.emailType,
    totalAmount: metadata?.totalAmount,
    invoiceCount: metadata?.invoiceCount,
    invoices: Array.isArray(metadata?.invoices) ? metadata.invoices : [],
  };
}

export function getCobranzaEmailHistoryEntries(agenda) {
  const notifications = Array.isArray(agenda?.notificationHistory) ? agenda.notificationHistory : [];
  const managementEmails = Array.isArray(agenda?.collectionManagementHistory)
    ? agenda.collectionManagementHistory.filter((entry) => entry?.eventType === "EMAIL_SENT")
    : [];

  const managementPool = managementEmails.map((entry, index) => ({
    index,
    used: false,
    ts: new Date(entry?.date || 0).getTime(),
    userEmail: String(entry?.userEmail || "").trim().toLowerCase(),
    data: getCobranzaEmailEntryFromManagementEvent(entry),
  }));

  const mergedNotifications = notifications.map((entry) => {
    const entryTs = new Date(entry?.date || 0).getTime();
    const entryUserEmail = String(entry?.userEmail || "").trim().toLowerCase();
    let bestMatch = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    managementPool.forEach((candidate) => {
      if (candidate.used) return;
      if (candidate.userEmail && entryUserEmail && candidate.userEmail !== entryUserEmail) return;
      const distance = Math.abs(candidate.ts - entryTs);
      if (distance > 10 * 60 * 1000) return;
      if (distance < bestDistance) {
        bestDistance = distance;
        bestMatch = candidate;
      }
    });

    if (!bestMatch) return entry;

    bestMatch.used = true;

    return {
      ...bestMatch.data,
      ...entry,
      ccEmails:
        Array.isArray(entry?.ccEmails) && entry.ccEmails.length > 0
          ? entry.ccEmails
          : bestMatch.data.ccEmails,
      emailRecipients: mergeEmailRecipientsForHistory(
        entry?.emailRecipients,
        bestMatch.data.emailRecipients,
      ),
      emailStatus: pickHigherCobranzaEmailStatus(entry?.emailStatus, bestMatch.data.emailStatus),
      emailStatusAt: entry?.emailStatusAt || bestMatch.data.emailStatusAt,
      invoices:
        Array.isArray(entry?.invoices) && entry.invoices.length > 0
          ? entry.invoices
          : bestMatch.data.invoices,
      subject: entry?.subject || bestMatch.data.subject,
      bodyHtml: entry?.bodyHtml || bestMatch.data.bodyHtml,
      recipientEmail: entry?.recipientEmail || bestMatch.data.recipientEmail,
      emailType: entry?.emailType || bestMatch.data.emailType,
      totalAmount: entry?.totalAmount ?? bestMatch.data.totalAmount,
      invoiceCount: entry?.invoiceCount ?? bestMatch.data.invoiceCount,
    };
  });

  const fallbackManagementEntries = managementPool
    .filter((candidate) => !candidate.used)
    .map((candidate) => candidate.data);

  return [...mergedNotifications, ...fallbackManagementEntries].sort(
    (a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime()
  );
}

/** Unifica historial de gestión + correos para la línea de tiempo del sidebar. */
export function buildCobranzaGestionHistoryItems(agenda, t) {
  const items = [];
  const mgmt = Array.isArray(agenda?.collectionManagementHistory)
    ? agenda.collectionManagementHistory.filter((entry) => entry?.eventType !== "EMAIL_SENT")
    : [];
  for (let i = 0; i < mgmt.length; i += 1) {
    const e = mgmt[i];
    const { dateLabel, timeLabel } = formatGestionHistoryDateTime(e?.date);
    items.push({
      id: `mgmt-${i}-${e?.date}-${e?.eventType}`,
      eventType: e?.eventType || "EVENT",
      userEmail: e?.userEmail || e?.userName || t("gestionHistory.defaultUser"),
      userName: e?.userName,
      dateLabel,
      timeLabel,
      details: formatGestionEventLabel(e, t),
      sortTs: new Date(e?.date || 0).getTime(),
    });
  }
  const emails = getCobranzaEmailHistoryEntries(agenda);
  for (let j = 0; j < emails.length; j += 1) {
    const e = emails[j];
    const { dateLabel, timeLabel } = formatGestionHistoryDateTime(e?.date);
    items.push({
      id: `email-${j}-${e?.date}`,
      eventType: "EMAIL_SENT",
      userEmail: e?.userEmail || t("gestionHistory.defaultUser"),
      dateLabel,
      timeLabel,
      details: getCobranzaEmailHistorySummary(e, t),
      emailLog: e,
      sortTs: new Date(e?.date || 0).getTime(),
    });
  }
  return items.sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0));
}

export function formatGestionEventLabel(entry, t) {
  if (!entry) return "";
  const dash = "—";
  const type = entry.eventType;
  if (type === "EMAIL_SENT") {
    const recipient = entry.metadata?.recipientEmail;
    const subj = entry.metadata?.subject;
    if (subj && recipient) return t("gestionHistory.emailToRecipient", { recipient, subject: subj });
    if (subj) return t("gestionHistory.emailWithSubject", { subject: subj });
    if (recipient) return t("gestionHistory.emailToRecipientOnly", { recipient });
    return t("gestionHistory.emailDefault");
  }
  if (type === "ASSIGNEE_CHANGED") {
    return t("gestionHistory.assigneeChanged", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "DECISION_CHANGED") {
    return t("gestionHistory.decisionChanged", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "CHANNEL_CHANGED") {
    return t("gestionHistory.channelChanged", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "RESPONSE_UPDATED") {
    const folio = entry.metadata?.documentId ? t("gestionHistory.folioSuffix") : "";
    return t("gestionHistory.responseUpdated", { folio, value: entry.newValue || dash });
  }
  if (type === "NEXT_STEPS_UPDATED") {
    return t("gestionHistory.nextStepsUpdated", { value: entry.newValue || dash });
  }
  if (type === "CLIENT_STATUS_UPDATED") {
    return t("gestionHistory.clientStatusUpdated", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "BUSINESS_LINE_UPDATED") {
    return t("gestionHistory.businessLineUpdated", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "BUSINESS_GROUP_UPDATED") {
    return t("gestionHistory.businessGroupUpdated", {
      from: entry.previousValue || dash,
      to: entry.newValue || dash,
    });
  }
  if (type === "DOCUMENTS_SYNCED") {
    return t("gestionHistory.documentsSynced", {
      value: entry.newValue || t("gestionHistory.invoicesDefault"),
    });
  }
  return entry.newValue || entry.eventType || t("gestionHistory.event");
}

function formatPaidInvoiceDiasLabel(count) {
  const n = Math.abs(Math.round(Number(count)));
  if (Number.isNaN(n)) return "—";
  if (n === 1) return "1 día";
  return `${n} días`;
}

export function formatPaidInvoiceHistoryLabel(item) {
  const label = item?.documentLabel || "Factura";
  const folio = item?.folio || item?.documentId;
  return folio ? `${label} #${folio}` : label;
}

export function getPaidInvoiceDsoPresentation(dsoDays) {
  if (dsoDays == null || dsoDays === "") {
    return { text: "—", className: "cf-dso-value--neutral" };
  }
  const num = Number(dsoDays);
  if (Number.isNaN(num)) {
    return { text: "—", className: "cf-dso-value--neutral" };
  }
  if (num < 0) {
    return {
      text: `${formatPaidInvoiceDiasLabel(num)} antes`,
      className: "cf-dso-value--early",
    };
  }
  if (num > 0) {
    return {
      text: `${formatPaidInvoiceDiasLabel(num)} atraso`,
      className: "cf-dso-value--late",
    };
  }
  return { text: "0 días", className: "cf-dso-value--neutral" };
}
